import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';

const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signInWithOTP, verifyOTP, demoLogin } = useAuth();
  const navigate = useNavigate();

  const handleDemoLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const { error: err } = await demoLogin('+919493871855');
      if (err) throw new Error(err);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const fullPhone = phone.startsWith('+') ? phone : `+91${phone}`;
      const { error } = await signInWithOTP(fullPhone);
      if (error) throw error;
      setStep('otp');
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const fullPhone = phone.startsWith('+') ? phone : `+91${phone}`;
      const { error } = await verifyOTP(fullPhone, otp);
      if (error) throw error;
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col"
      style={{ fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif", background: '#000' }}>

      {/* Minimal header */}
      <header className="p-6 flex items-center justify-center">
        <div className="flex items-center gap-0.5" data-testid="noteho-logo">
          <span className="text-2xl font-bold tracking-[-0.04em] text-white">NOTE</span>
          <span className="text-2xl font-bold tracking-[-0.04em]"
            style={{ background: 'linear-gradient(135deg, #FF2D55, #5856D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            HO
          </span>
        </div>
      </header>

      {/* Login card */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm" data-testid="login-card">

          <div className="text-center mb-10">
            <h1 className="text-4xl font-semibold tracking-[-0.03em] text-white mb-3">
              Sign in.
            </h1>
            <p className="text-[#86868B] text-base">
              Enter your phone number to continue.
            </p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} className="space-y-5">
              <div className="relative">
                <div className="flex items-center rounded-xl overflow-hidden"
                  style={{ background: '#1D1D1F', border: '1px solid #333' }}>
                  <span className="pl-4 pr-2 text-[#86868B] text-sm select-none">+91</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Phone number"
                    className="flex-1 bg-transparent text-white text-base py-3.5 pr-4 outline-none placeholder:text-[#48484A]"
                    data-testid="phone-input"
                    autoFocus
                  />
                </div>
              </div>

              {error && <p className="text-[#FF453A] text-sm text-center" data-testid="error-message">{error}</p>}

              <button
                type="submit"
                disabled={loading || phone.length < 10}
                className="w-full py-3.5 rounded-xl text-white text-base font-medium transition-all disabled:opacity-40"
                style={{ background: loading || phone.length < 10 ? '#333' : 'linear-gradient(135deg, #FF2D55, #5856D6)' }}
                data-testid="send-otp-btn"
              >
                {loading ? 'Sending...' : 'Continue'}
              </button>

              {/* Demo Login */}
              <div className="pt-2" style={{ borderTop: '1px solid #1D1D1F' }}>
                <button
                  type="button"
                  onClick={() => handleDemoLogin()}
                  className="w-full py-3 rounded-xl text-sm transition-all hover:opacity-80"
                  style={{ background: '#1D1D1F', color: '#8E8E93', border: '1px solid #2C2C2E' }}
                  data-testid="demo-login-btn"
                >
                  Demo Login (Raju Seth)
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div className="text-center">
                <p className="text-[#86868B] text-sm mb-1">Enter OTP sent to +91{phone}</p>
                <p className="text-[#48484A] text-xs">Use code: 123456</p>
              </div>

              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  data-testid="otp-input"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="w-12 h-14 rounded-xl text-lg font-medium text-white border-0"
                        style={{ background: '#1D1D1F' }}
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error && <p className="text-[#FF453A] text-sm text-center">{error}</p>}

              <button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full py-3.5 rounded-xl text-white text-base font-medium transition-all disabled:opacity-40"
                style={{ background: loading || otp.length < 6 ? '#333' : 'linear-gradient(135deg, #FF2D55, #5856D6)' }}
                data-testid="verify-otp-btn"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <button
                type="button"
                className="w-full text-sm text-[#0A84FF] hover:underline"
                onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
              >
                Change Number
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-[#48484A] text-xs">
          Built by Noteho Technologies
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
